import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: "website" | "article" | "profile";
  lang?: "it" | "en" | "es";
  schema?: object;
  twitterSite?: string;
  articleAuthor?: string;
  noIndex?: boolean;
}

export const SEO = ({
  title,
  description,
  image,
  imageWidth = 1200,
  imageHeight = 630,
  type = "website",
  lang = "it",
  schema,
  twitterSite = "@inclusia_it",
  articleAuthor,
  noIndex = false
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = window.location.origin;
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const fullUrl = `${siteUrl}${location.pathname}`;
  const fullTitle = `${title} | Inclusia`;
  const ogImage = image || defaultImage;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, property = false) => {
      const attribute = property ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute("content", content);
    };

    // Basic meta tags
    updateMeta("description", description);
    
    // Robots meta tag
    if (noIndex) {
      updateMeta("robots", "noindex, nofollow");
    } else {
      updateMeta("robots", "index, follow");
    }
    
    // Open Graph
    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", description, true);
    updateMeta("og:image", ogImage, true);
    updateMeta("og:image:width", String(imageWidth), true);
    updateMeta("og:image:height", String(imageHeight), true);
    updateMeta("og:image:alt", title, true);
    updateMeta("og:url", fullUrl, true);
    updateMeta("og:type", type === "profile" ? "profile" : type, true);
    updateMeta("og:site_name", "Inclusia", true);
    updateMeta("og:locale", `${lang}_${lang.toUpperCase()}`, true);

    // Twitter Card
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:site", twitterSite);
    updateMeta("twitter:title", fullTitle);
    updateMeta("twitter:description", description);
    updateMeta("twitter:image", ogImage);
    updateMeta("twitter:image:alt", title);

    // Article author (for profiles/articles)
    if (articleAuthor) {
      updateMeta("article:author", articleAuthor, true);
    }

    // Language
    document.documentElement.lang = lang;

    // Canonical link (only for indexed pages)
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (noIndex) {
      // Remove canonical for noindex pages
      if (canonical) {
        canonical.remove();
      }
    } else {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = fullUrl;
    }

    // Schema.org structured data
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!schemaScript) {
        schemaScript = document.createElement("script");
        schemaScript.type = "application/ld+json";
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }
  }, [title, description, image, imageWidth, imageHeight, type, lang, fullUrl, fullTitle, schema, twitterSite, articleAuthor, ogImage]);

  return null;
};
